import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useMemo,
  useEffect,
} from 'react';
import { ISendFormatada } from '../components/Chat';

import api from '../services/api';
import getResponseErrors from '../utils/getResponseErrors';
import { useToast } from './toast';

export interface IMessage {
  clinica: string;
  from: IUser;
  idFrom: number;
  to: string;
  idTo: number;
  mensagem: string;
  lida: boolean;
  minha?: boolean;
  created_at?: Date;
}

export interface IUser {
  UsuCodigo: number;
  UsuNome: string;
  UsuEmail: string;
  avatar_url: string;
  UsuTipo: number;
  UsuClinica: number;
  naoLidas?: number;
  mensagens?: IMessage[];
  novaMensagem?: boolean;
}

export interface IUsuario {
  usuCodigo: string;
  usuNome: string;
  sessionId: string;
  clinica: string;
  codClinica: string;
  pagina: string;
  socketId?: string;
  usuTipo: number;
}

export interface IMensagensRecebidas {
  userId: number;
  count: string;
}

interface ChatContextData {
  users: IUser[];
  mensagens: ISendFormatada[] | undefined;
  naoLidas: number;
  usuarioSelecionado: IUser | undefined;
  setUsuarioSelecionado(usuario: IUser | undefined): void;
  buscarUsuarios(userId: number): void;
  atualizaNaoLidas(contatoId: number): void;
  addMensagem(data: ISendFormatada): void;
  addMensagens(data: ISendFormatada[] | undefined): void;
  addOldMensagem(data: ISendFormatada[]): void;
}

const ChatContext = createContext<ChatContextData>({} as ChatContextData);

const ChatProvider: React.FC = ({ children }) => {
  const { addToast } = useToast();
  const [users, setUsers] = useState<IUser[]>([]);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState<
    IUser | undefined
  >(undefined);

  const [mensagens, setMensagens] = useState<ISendFormatada[] | undefined>(
    undefined,
  );

  const naoLidas = useMemo(() => {
    return users.reduce((tot, usu) => {
      return tot + (usu.naoLidas ? Number(usu.naoLidas) : 0);
    }, 0);
  }, [users]);

  const addMensagem = useCallback((data: ISendFormatada) => {
    setMensagens(state => {
      return state ? state.map(mens => mens).concat([data]) : [data];
    });
  }, []);

  const addOldMensagem = useCallback((data: ISendFormatada[]) => {
    setMensagens(state => {
      return state ? data.concat(state) : data;
    });
  }, []);

  const addMensagens = useCallback((data: ISendFormatada[] | undefined) => {
    setMensagens(data);
  }, []);

  useEffect(() => {
    if (usuarioSelecionado) {
      setUsers(state => {
        const usuAtual = state.find(
          us => us.UsuCodigo === usuarioSelecionado.UsuCodigo,
        );
        if (usuAtual) {
          usuAtual.novaMensagem = false;
          usuAtual.naoLidas = 0;
        }
        return usuAtual
          ? [usuAtual].concat(
              state.filter(us => us.UsuCodigo !== usuarioSelecionado.UsuCodigo),
            )
          : state;
      });
    }
  }, [usuarioSelecionado]);

  const buscarUsuarios = useCallback(
    async (userId: number) => {
      try {
        const response = await api.get(
          `users/listarUsuariosChat?except_user_id=${userId}`,
        );

        const outrosUsuarios = response.data.filter(
          (us: IUser) => us.UsuCodigo !== userId,
        );

        const responseMensagens = await api.get<IMensagensRecebidas[]>(
          `mensagem/naolidas?contatoId=${userId}`,
        );

        const usuariosNaoLidas = outrosUsuarios.map(
          (usu: IUser): IUser => {
            const filtroNaoLidas = responseMensagens.data.filter(
              (mens: IMensagensRecebidas) => mens.userId === usu.UsuCodigo,
            )[0]
              ? Number(
                  responseMensagens.data.filter(
                    (mens: IMensagensRecebidas) =>
                      mens.userId === usu.UsuCodigo,
                  )[0].count,
                )
              : 0;
            return {
              ...usu,
              naoLidas: filtroNaoLidas,
            };
          },
        );

        setUsers(usuariosNaoLidas);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar usuários',
          type: 'error',
          description: mensagemErro,
        });
      }
    },
    [addToast],
  );

  const atualizaNaoLidas = useCallback((contatoId: number) => {
    setUsers(state => {
      if (!state) return [];

      const usuario = state.find(usu => usu.UsuCodigo === contatoId);
      if (!usuario) return state;
      usuario.naoLidas = usuario.naoLidas ? usuario.naoLidas + 1 : 1;
      return state
        ? [usuario].concat(state.filter(usu => usu.UsuCodigo !== contatoId))
        : [usuario];
    });
  }, []);

  return (
    <ChatContext.Provider
      value={{
        users,
        naoLidas,
        usuarioSelecionado,
        mensagens,
        setUsuarioSelecionado,
        buscarUsuarios,
        atualizaNaoLidas,
        addMensagem,
        addMensagens,
        addOldMensagem,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

function useChat(): ChatContextData {
  const context = useContext(ChatContext);

  if (!context) {
    throw new Error('useChat deve ser usado com o ChatProvider');
  }

  return context;
}

export { ChatProvider, useChat };
